import {
  BUSINESS_LICENSE_REPORT_JOURNEY_PATH,
  EIN_CREATION_JOURNEY_PATH,
  INITIAL_REPORT_JOURNEY_PATH,
  LLC_FORMATION_JOURNEY_PATH,
  OPERATING_AGREEMENT_JOURNEY_PATH,
  REGISTERED_AGENT_SERVICE_JOURNEY_PATH,
} from '#client/constants';

// Recursively freezes the config so the buckets and entries (not just the top
// level) are immutable — `Object.freeze` alone is shallow.
const deepFreeze = (obj) => {
  Object.values(obj).forEach((value) => {
    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  });
  return Object.freeze(obj);
};

/**
 * Canonical service-code → route config for the whole app, consumed by the single
 * `useUrlResolver` hook. Grouped by the context that resolves the URL, because the
 * same service code legitimately resolves to different destinations pre-purchase
 * (product_cta) vs. post-purchase (checkout_page / checkout_slide / enterprise).
 *
 * Entry schema (each entry declares only what it needs):
 *   kind: 'dashboard' | 'external' | 'journey' | 'current'
 *     - dashboard: `${dashboardPath}${basePath}` (an internal dashboard page)
 *     - external:  `new URL(basePath, window.location)` with query params applied
 *     - journey:   `generatePath(basePath, { businessEntityUuid })` (relative href)
 *     - current:   the current `window.location.pathname` (slide "stay here" entries)
 *   basePath: string (may contain the `:businessEntityUuid` template) OR
 *             (businessEntityUuid, isFlagEnabled) => string  (feature-flag branching)
 *   options:  for external entries — `idAliases` (array of query-param KEY names each
 *             set to the entity id), `idAlias`/`orderIdAlias` (single keys), and any
 *             other key is a literal extra query param. These KEY names are the
 *             receiving app's contract (shopping-ui `/r/...`, Chase, Microsoft) — not
 *             internal drift — so they are kept verbatim. May also be a function with
 *             the same (businessEntityUuid, isFlagEnabled) => object signature as
 *             basePath, for an entry whose query params should only apply on one
 *             branch (e.g. a flag-gated destination shouldn't also decorate its own
 *             legacy fallback with query params the legacy URL never had).
 *   searchParamDelimiter: replaces `&` in the query string (the one Microsoft URL).
 *   forceReload: consumed only by the checkout_slide adapter (hard nav vs. SPA nav).
 */
export const SERVICE_ROUTES = deepFreeze({
  product_cta: {
    annual_report: { kind: 'dashboard', basePath: '/compliance/annual-reports' },
    basic_email: { kind: 'dashboard', basePath: '/marketing-tools/emails' },
    beneficial_ownership_filing: { kind: 'dashboard', basePath: '/f/go/complete_beneficial_ownership_filing' },
    business_credit_cards: {
      kind: 'external',
      basePath: 'https://creditcards.chase.com/a1/DAO/Ink-Homepage/Cash1124',
      options: {
        CELL: '6H8X',
        AFFID: 'A_HiQlD3Yhw-mCLb_axDjz0K9L24JhoJPg',
        idAliases: ['pvid'],
        jp_cmp: 'cc/1754462/aff/3-10004686/na',
      },
    },
    business_licenses: { kind: 'dashboard', basePath: '/compliance/business-licenses' },
    // intentional: pre-purchase (product_cta) sends to the compliance page; post-purchase
    // (checkout_page) sends to the fulfillment journey. The two moments legitimately differ.
    business_license_report: { kind: 'dashboard', basePath: '/compliance/business-licenses' },
    business_license_filing: { kind: 'dashboard', basePath: '/compliance/business-licenses/filing-details' },
    change_of_registered_agent: {
      kind: 'external',
      basePath: '/r/register-cora/init',
      options: {
        idAliases: ['companyId'],
        fromLinkModal: true,
        fromDashboard: true,
      },
    },
    constant_contact: {
      kind: 'external',
      basePath: 'https://constant-contact.ibfwsl.net/c/3633958/905514/3411',
      options: {
        idAliases: ['subId1', 'sharedid'],
        subId2: '30DayJourneyEmail',
      },
    },
    corp_amendment: {
      kind: 'external',
      basePath: '/r/amendment/init',
      options: {
        idAliases: ['business_entity_uuid'],
        fromDashboard: true,
      },
    },
    domain_name_reg: { kind: 'dashboard', basePath: '/marketing-tools/domains' },
    domain_name_reg_sqsp: { kind: 'dashboard', basePath: '/marketing-tools/domains/search' },
    fundbox: {
      kind: 'external',
      basePath: 'https://app.fundbox.com/signup',
      options: {
        campaign_source: 'ZenBusiness',
        campaign_name: 'ZenBusiness',
      },
    },
    gusto_referral: { kind: 'external', basePath: 'https://gusto.com/go/bd/zenbusiness?idgm=m1w2pyol' },
    ipostal1: {
      kind: 'external',
      basePath: 'https://ipostal1.com/virtual-business-address-plans-pricing.php?ref=6743',
    },
    legal_shield: { kind: 'external', basePath: 'https://legalshield.sjv.io/kOzGvd' },
    llc_amendment: {
      kind: 'external',
      basePath: '/r/amendment/init',
      options: {
        idAliases: ['business_entity_uuid'],
        fromDashboard: true,
      },
    },
    logo_kit: { kind: 'external', basePath: '/lb/a/wizard' },
    microsoft: {
      kind: 'external',
      basePath:
        'https://www.microsoft.com/en-us/microsoft-365/business/zenbusiness-partner-offer?ocid=cmmdpc9xzvo?zbid=%5BUUID%5D',
      searchParamDelimiter: '?',
      options: {
        ocid: 'cmmtyxdgaec',
        idAliases: ['zbid'],
      },
    },
    next_insurance: {
      kind: 'external',
      basePath: 'https://nextinsurance.sjv.io/ZQmD1z',
      options: {
        irclickid: 'TGdTA9QZtxyKUG53iwSRIwmAUks1-TzUv3zq1k0',
        channel: 'affiliate_direct',
        utm_source: 'impact',
        utm_campaign: 'ZenBusiness',
        adid: '1148969',
        campaignid: '14516',
        irgwc: '1',
        serial: '992855780',
        prev_tracking_id: 'df967d774689fc46079c5a4c476509b2',
        tracking_id: '26a417f2935656c49d147f644e8a9f87',
      },
    },
    ocoya: { kind: 'external', basePath: 'https://www.ocoya.com/?via=zenbusiness' },
    registered_agent: {
      kind: 'external',
      basePath: '/r/register-cora/init',
      options: {
        idAliases: ['companyId'],
        fromLinkModal: true,
        fromDashboard: true,
      },
    },
    zb_tax_advice_biztax_prep: {
      kind: 'external',
      basePath: '/r/tax-services/init',
      options: {
        idAliases: ['companyId'],
        fromDashboard: true,
        product: 'zb_tax_advice_biztax_prep',
      },
    },
    zb_tax_advice: {
      kind: 'external',
      basePath: '/r/tax-services/init',
      options: {
        idAliases: ['companyId'],
        fromDashboard: true,
        product: 'zb_tax_advice',
      },
    },
    zb_money_pro_month: {
      kind: 'external',
      basePath: '/r/register-zbmpro/init',
      options: {
        idAliases: ['companyId'],
        isMonthly: true,
        fromDashboard: true,
      },
    },
    zb_money_pro_year: {
      kind: 'external',
      basePath: '/r/register-zbmpro/init',
      options: {
        idAliases: ['companyId'],
        isMonthly: false,
        fromDashboard: true,
      },
    },
    velo_prime: { kind: 'external', basePath: 'https://zenbiz.com' },
    website_tools_bundle: {
      kind: 'external',
      basePath: '/r/onlinetools-registration/init',
      options: {
        idAliases: ['companyId'],
        fromDashboard: true,
      },
    },
    wfg_initial_report: {
      kind: 'external',
      basePath: '/r/register-wfgir/init',
      options: {
        idAliases: ['companyId'],
        fromDashboard: true,
      },
    },
    corp_formation: {
      kind: 'external',
      basePath: '/shop/corp',
      options: {
        entryUri: '/dashboard',
      },
    },
    llc_formation: { kind: 'external', basePath: '/shop/llc-velo/' },
    shopify: {
      kind: 'external',
      basePath: 'https://shopify.pxf.io/9VM1BY',
      options: {
        idAliases: ['subId1'],
        subId2: 'dashboard/Velo',
      },
    },
    sales_tax_registration: {
      kind: 'external',
      basePath: 'https://interview.businesslicenses.com/checkout/5icrjcaberpzxss',
    },
    s_corp_designator: { kind: 'external', basePath: '/shop/s-corp' },
    static_website: { kind: 'dashboard', basePath: '/marketing-tools/websites' },
    website_sqsp_core: { kind: 'dashboard', basePath: '/marketing-tools/websites/select-plan' },
    bank_of_america_offer: { kind: 'dashboard', basePath: '/finances?boa_modal=1' },
  },

  checkout_page: {
    // intentional: post-purchase (checkout_page) sends to the fulfillment journey;
    // pre-purchase (product_cta) sends to the compliance page. The two moments legitimately differ.
    business_license_report: {
      kind: 'external',
      basePath: BUSINESS_LICENSE_REPORT_JOURNEY_PATH,
    },
    // isFlagEnabled (resolved by the caller from journeys-api-v4-services +
    // journeys-v4-redirection-annual-report — see OrderTracker.js) sends a v4-eligible
    // task to the version-agnostic v4 discovery route instead of the legacy v3 journey.
    // options is also flag-gated: the legacy URL never carried query params, and other
    // callers (e.g. usePageCheckoutComplete, which never passes this flag) still expect
    // that exact shape.
    llc_annual_report: {
      kind: 'external',
      basePath: (_businessEntityUuid, isFlagEnabled) =>
        isFlagEnabled ? '/journeys/next' : '/journeys/annual-report-filing/:businessEntityUuid',
      options: (_businessEntityUuid, isFlagEnabled) =>
        isFlagEnabled ? { idAlias: 'businessEntityUuid', orderIdAlias: 'orderUuid' } : undefined,
    },
    operating_agreement: {
      kind: 'external',
      basePath: OPERATING_AGREEMENT_JOURNEY_PATH,
    },
    trademarks_search: { kind: 'dashboard', basePath: '/compliance/trademarks' },
    beneficial_ownership_filing: {
      kind: 'external',
      basePath: '/f/go/complete_beneficial_ownership_filing',
      options: {
        idAlias: 'businessEntityUuid',
        orderIdAlias: 'orderUuid',
      },
    },
    domain_name_reg: {
      kind: 'external',
      basePath: '/r/domainname-registration-v2/init',
      options: {
        idAlias: 'companyId',
        orderIdAlias: 'orderUuid',
      },
    },
    zb_tax_advice: { kind: 'dashboard', basePath: '/money/tax-services' },
    zb_tax_advice_biztax_prep: { kind: 'dashboard', basePath: '/money/tax-services' },
    llc_amendment: {
      kind: 'external',
      basePath: '/journeys/llc-amendment/:businessEntityUuid',
    },
    ein_creation: {
      kind: 'external',
      basePath: EIN_CREATION_JOURNEY_PATH,
    },
    llc_formation: {
      kind: 'external',
      basePath: LLC_FORMATION_JOURNEY_PATH,
    },
    wfg_initial_report: {
      kind: 'external',
      basePath: INITIAL_REPORT_JOURNEY_PATH,
    },
    // Flag-gated like llc_annual_report: v4 discovery when on, legacy route (and options) when off.
    corp_formation: {
      kind: 'external',
      basePath: (_businessEntityUuid, isFlagEnabled) => (isFlagEnabled ? '/journeys/next' : '/r/complete-corp'),
      options: (_businessEntityUuid, isFlagEnabled) =>
        isFlagEnabled ? { idAlias: 'businessEntityUuid', orderIdAlias: 'orderUuid' } : undefined,
    },
    // Delaware corp formation completes via share setup instead of /r/complete-corp;
    // the OrderTracker selects this service code when the corp task's jurisdiction is DE.
    // Gated by the same corp eligibility flag as corp_formation.
    corp_shares_setup: {
      kind: 'external',
      basePath: (_businessEntityUuid, isFlagEnabled) => (isFlagEnabled ? '/journeys/next' : '/r/stock-setup/init'),
      options: (_businessEntityUuid, isFlagEnabled) =>
        isFlagEnabled ? { idAlias: 'businessEntityUuid', orderIdAlias: 'orderUuid' } : { idAliases: ['companyId'] },
    },
    registered_agent_service: {
      kind: 'external',
      basePath: REGISTERED_AGENT_SERVICE_JOURNEY_PATH,
    },
    // Post-purchase CoRA resumes through its own route. The pre-purchase
    // register-cora flow redirects into a questionnaire that starts the order over.
    change_of_registered_agent: {
      kind: 'external',
      basePath: '/r/complete-cora/start',
    },
  },

  checkout_slide: {
    beneficial_ownership_filing: { kind: 'dashboard', basePath: '/f/go/complete_beneficial_ownership_filing' },
    business_kit: { kind: 'dashboard', basePath: '' },
    corporate_docs: { kind: 'current', forceReload: true },
    standard_good_standing: { kind: 'dashboard', basePath: '' },
    trademarks_search: { kind: 'dashboard', basePath: '/compliance/trademarks', forceReload: true },
    business_license_filing: {
      kind: 'dashboard',
      basePath: '/compliance/business-licenses/filing-details',
      forceReload: true,
    },
    // Same fulfillment journey as checkout_page. forceReload because the journey lives
    // outside the dashboard SPA — the slide adapter would otherwise SPA-navigate to it.
    business_license_report: {
      kind: 'external',
      basePath: BUSINESS_LICENSE_REPORT_JOURNEY_PATH,
      forceReload: true,
    },
    // Same fulfillment journey as checkout_page, and forceReload for the same reason.
    operating_agreement: {
      kind: 'external',
      basePath: OPERATING_AGREEMENT_JOURNEY_PATH,
      forceReload: true,
    },
    wfg_initial_report: { kind: 'dashboard', basePath: '/compliance/initial-reports' },
    zb_tax_advice: { kind: 'dashboard', basePath: '/money/tax-services' },
    zb_tax_advice_biztax_prep: { kind: 'dashboard', basePath: '/money/tax-services' },
  },

  enterprise: {
    business_license_report: { kind: 'journey', basePath: BUSINESS_LICENSE_REPORT_JOURNEY_PATH },
    ein_creation: { kind: 'journey', basePath: EIN_CREATION_JOURNEY_PATH },
    // Discovery needs the orderUuid to resolve v4; flag off → empty path, status-only row.
    llc_annual_report: {
      kind: 'external',
      basePath: (_businessEntityUuid, isFlagEnabled) => (isFlagEnabled ? '/journeys/next' : ''),
      options: { idAlias: 'businessEntityUuid', orderIdAlias: 'orderUuid' },
    },
    llc_formation: { kind: 'journey', basePath: LLC_FORMATION_JOURNEY_PATH },
    operating_agreement: { kind: 'journey', basePath: OPERATING_AGREEMENT_JOURNEY_PATH },
  },
});
